import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, RotateCcw, X, XCircle, Trash2 } from "lucide-react";
import {
  secondaryContractsApi,
  type SecondaryEmploymentContract,
  type SecondaryContractRateType,
} from "@/features/finances/api/secondaryContracts";
import { groupFinanceApi } from "@/features/group_finance/api/groupFinance";
import { contractScansApi } from "@/features/finances/api/contractScans";
import { ContractScanField } from "@/features/finances/components/ContractScanField";
import { uploadErrorMessage } from "@/utils/managedFile";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";

interface Props {
  employeeId: string;
  canManage: boolean;
  /** Scan upload/replace only — HR (kadrowa) has this without full canManage. */
  canManageScans?: boolean;
  primaryContractType: "uop" | "uz" | "b2b";
  employeeName: string;
}

const SECONDARY_QUERY_KEY = (employeeId: string) => ["secondary-contracts", employeeId];

export function SecondaryEmploymentContractsPanel({
  employeeId,
  canManage,
  canManageScans = canManage,
  primaryContractType,
  employeeName,
}: Props) {
  const qc = useQueryClient();
  // null = form closed; "new" = create; otherwise the id of the contract being edited.
  const [formTarget, setFormTarget] = useState<null | "new" | string>(null);
  const showForm = formTarget !== null;
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  const [rateType, setRateType] = useState<SecondaryContractRateType>("hourly");
  const [hourlyRate, setHourlyRate] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const { data: companies = [] } = useQuery({
    queryKey: ["company-choices"],
    queryFn: () => groupFinanceApi.getCompanyChoices(),
    staleTime: 5 * 60_000,
  });

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: SECONDARY_QUERY_KEY(employeeId),
    queryFn: () => secondaryContractsApi.list(employeeId),
  });

  const resetForm = () => {
    setFormTarget(null);
    setEffectiveFrom("");
    setEffectiveTo("");
    setRateType("hourly");
    setHourlyRate("");
    setCompanyId("");
    setNote("");
    setFormError(null);
  };

  const onSaveError = (err: unknown) => {
    const detail =
      // @ts-expect-error axios response shape
      err?.response?.data?.errors?.detail ??
      // @ts-expect-error
      Object.values(err?.response?.data?.errors ?? {}).flat?.()?.[0] ??
      "Nie udało się zapisać dodatkowej umowy.";
    setFormError(String(detail));
  };

  const create = useMutation({
    mutationFn: (payload: {
      effective_from: string;
      effective_to?: string | null;
      rate_type: SecondaryContractRateType;
      hourly_gross_rate: string;
      company: string;
      note?: string;
    }) =>
      secondaryContractsApi.create({
        employee: employeeId,
        kind: "uz",
        ...payload,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SECONDARY_QUERY_KEY(employeeId) });
      resetForm();
    },
    onError: onSaveError,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      effective_from: string;
      effective_to?: string | null;
      rate_type: SecondaryContractRateType;
      hourly_gross_rate: string;
      company: string;
      note?: string;
    }) => secondaryContractsApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SECONDARY_QUERY_KEY(employeeId) });
      resetForm();
    },
    onError: onSaveError,
  });

  const end = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string }) =>
      secondaryContractsApi.end(id, date),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: SECONDARY_QUERY_KEY(employeeId) }),
  });

  // Reactivation: clear effective_to (multiple open UZ contracts are allowed).
  const reactivate = useMutation({
    mutationFn: (id: string) =>
      secondaryContractsApi.update(id, { effective_to: null }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: SECONDARY_QUERY_KEY(employeeId) }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => secondaryContractsApi.remove(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: SECONDARY_QUERY_KEY(employeeId) }),
  });

  // Two-step: upload the PDF to /files/, then attach it to the contract.
  const uploadScan = useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const managedFile = await contractScansApi.uploadPdf(file);
      return secondaryContractsApi.setScan(id, managedFile.id);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: SECONDARY_QUERY_KEY(employeeId) }),
  });

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const isUzPrimary = primaryContractType === "uz";

  useEffect(() => {
    if (!showForm) setFormError(null);
  }, [showForm]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide">
            Dodatkowa umowa UZ
          </h4>
          <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
            {employeeName ? `Pracownika ${employeeName}` : "Pracownika"}
            {" — równolegle do podstawowego zatrudnienia."}
          </p>
        </div>
        {canManage && !isUzPrimary && !showForm && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setFormTarget("new")}
          >
            <Plus size={14} className="mr-1" /> Dodaj
          </Button>
        )}
      </div>

      {isUzPrimary && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Pracownik ma UZ jako podstawowy typ zatrudnienia — dodatkowa UZ niedostępna.
        </p>
      )}

      {/* Form */}
      {canManage && showForm && !isUzPrimary && (
        <div className="rounded-lg border border-[var(--color-border)] bg-white dark:bg-gray-800 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium text-[var(--color-text-primary)]">
              {formTarget === "new" ? "Nowa umowa UZ" : "Edycja umowy UZ"}
            </h5>
            <button
              type="button"
              onClick={resetForm}
              className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]"
              aria-label="Zamknij"
            >
              <X size={16} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldDate
              label="Obowiązuje od"
              required
              value={effectiveFrom}
              onChange={setEffectiveFrom}
            />
            <FieldDate
              label="Obowiązuje do"
              hint="opcjonalnie"
              value={effectiveTo}
              onChange={setEffectiveTo}
            />
            <div className="sm:col-span-2">
              <span className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide block mb-1">
                Typ stawki<span className="text-[var(--color-primary)] ml-0.5">*</span>
              </span>
              <div className="flex rounded border border-[var(--color-border)] overflow-hidden w-fit">
                <button
                  type="button"
                  onClick={() => setRateType("hourly")}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    rateType === "hourly"
                      ? "bg-[var(--color-primary)] text-white font-medium"
                      : "bg-white dark:bg-gray-800 text-[var(--color-text-secondary)] hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  Godzinowa
                </button>
                <button
                  type="button"
                  onClick={() => setRateType("monthly")}
                  className={`px-3 py-1.5 text-sm border-l border-[var(--color-border)] transition-colors ${
                    rateType === "monthly"
                      ? "bg-[var(--color-primary)] text-white font-medium"
                      : "bg-white dark:bg-gray-800 text-[var(--color-text-secondary)] hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  Miesięczna
                </button>
              </div>
            </div>
            <FieldNumber
              label="Stawka brutto"
              required
              suffix={rateType === "monthly" ? "zł/miesiąc" : "zł/h"}
              value={hourlyRate}
              onChange={setHourlyRate}
            />
            <FieldSelect
              label="Spółka GK (wypłacająca)"
              required
              value={companyId}
              onChange={setCompanyId}
              placeholder="Wybierz spółkę…"
              options={companies.map((c) => ({
                value: c.id,
                label: `${c.short_code} — ${c.name}`,
              }))}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide block mb-1">
              Uwagi
            </label>
            <textarea
              className="w-full rounded border border-[var(--color-border)] bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] resize-none"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="np. okres próbny, projekt, zakres prac…"
            />
          </div>

          {formError && (
            <p className="text-xs text-red-600 dark:text-red-400">{formError}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={resetForm}
            >
              Anuluj
            </Button>
            <Button
              type="button"
              size="sm"
              loading={create.isPending || update.isPending}
              disabled={!effectiveFrom || !hourlyRate || !companyId}
              onClick={() => {
                const payload = {
                  effective_from: effectiveFrom,
                  effective_to: effectiveTo || null,
                  rate_type: rateType,
                  hourly_gross_rate: hourlyRate,
                  company: companyId,
                  note,
                };
                if (formTarget === "new") create.mutate(payload);
                else if (formTarget) update.mutate({ id: formTarget, ...payload });
              }}
            >
              Zapisz umowę
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">Ładowanie…</p>
      ) : contracts.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic">
          Brak dodatkowych umów dla tego pracownika.
        </p>
      ) : (
        <div className="bg-white dark:bg-gray-800 overflow-x-auto">
          <table className="w-full text-xs min-w-[600px]">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Okres
                </th>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Spółka
                </th>
                <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Stawka
                </th>
                <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Skan
                </th>
                <th className="px-3 py-2 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {contracts.map((c) => (
                <ContractRow
                  key={c.id}
                  contract={c}
                  today={today}
                  canManage={canManage}
                  canManageScans={canManageScans}
                  endPending={end.isPending}
                  removePending={remove.isPending}
                  reactivatePending={reactivate.isPending}
                  scanUploading={
                    uploadScan.isPending && uploadScan.variables?.id === c.id
                  }
                  onScanSelect={(file) => uploadScan.mutate({ id: c.id, file })}
                  scanError={
                    uploadScan.isError && uploadScan.variables?.id === c.id
                      ? uploadErrorMessage(uploadScan.error, "Nie udało się wgrać skanu — spróbuj ponownie.")
                      : null
                  }
                  onEnd={() => end.mutate({ id: c.id, date: today })}
                  onEdit={() => {
                    setFormTarget(c.id);
                    setEffectiveFrom(c.effective_from);
                    setEffectiveTo(c.effective_to ?? "");
                    setRateType(c.rate_type);
                    setHourlyRate(c.hourly_gross_rate);
                    setCompanyId(c.company ?? "");
                    setNote(c.note ?? "");
                  }}
                  onReactivate={() => {
                    if (
                      window.confirm("Przywrócić umowę jako aktywną (bez daty końca)?")
                    ) {
                      reactivate.mutate(c.id);
                    }
                  }}
                  onDelete={() => {
                    if (
                      window.confirm(
                        "Usunąć dodatkową umowę UZ?\n\nDziałanie nieodwracalne — jeśli istnieją powiązane karty godzin, backend zablokuje usunięcie."
                      )
                    ) {
                      remove.mutate(c.id);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────────────

function ContractRow({
  contract,
  today,
  canManage,
  canManageScans,
  endPending,
  removePending,
  reactivatePending,
  scanUploading,
  onScanSelect,
  scanError,
  onEnd,
  onEdit,
  onReactivate,
  onDelete,
}: {
  contract: SecondaryEmploymentContract;
  today: string;
  canManage: boolean;
  canManageScans: boolean;
  endPending: boolean;
  removePending: boolean;
  reactivatePending: boolean;
  scanUploading: boolean;
  onScanSelect: (file: File) => void;
  scanError: string | null;
  onEnd: () => void;
  onEdit: () => void;
  onReactivate: () => void;
  onDelete: () => void;
}) {
  const isOpen = contract.effective_to === null;
  const isFuture = contract.effective_from > today;
  const isPast =
    !isOpen && contract.effective_to !== null && contract.effective_to < today;

  const status: "active" | "future" | "past" = isPast
    ? "past"
    : isFuture
    ? "future"
    : "active";

  const STATUS_CONFIG = {
    active: {
      label: "Aktywna",
      cls: "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
    },
    future: {
      label: "Zaplanowana",
      cls: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
    },
    past: {
      label: "Zakończona",
      cls: "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600",
    },
  };
  const cfg = STATUS_CONFIG[status];

  const okresText = `${formatDate(contract.effective_from)} — ${formatDate(contract.effective_to, "∞")}`;

  return (
    <tr
      className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
        status === "past" ? "opacity-50" : ""
      }`}
      title={contract.note ? `Uwagi: ${contract.note}` : undefined}
    >
      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
        {okresText}
      </td>
      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
        {contract.company_short_code || contract.company_name || (
          <span className="text-gray-300 italic">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono font-medium text-gray-900 dark:text-gray-100">
        {formatAmount(contract.hourly_gross_rate)}{" "}
        {contract.rate_type === "monthly" ? "zł/mies." : "zł/h"}
      </td>
      <td className="px-3 py-2 text-center">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${cfg.cls}`}
        >
          {cfg.label}
        </span>
      </td>
      <td className="px-3 py-2">
        <ContractScanField
          scan={
            contract.contract_scan
              ? {
                  url: contract.contract_scan_url,
                  original_filename:
                    contract.contract_scan_detail?.original_filename ?? "skan.pdf",
                }
              : null
          }
          canManage={canManageScans}
          uploading={scanUploading}
          onSelect={onScanSelect}
          uploadError={scanError}
          compact
        />
      </td>
      <td className="px-3 py-2">
        {canManage && (
          <div className="flex items-center justify-end gap-0.5">
            <button
              type="button"
              onClick={onEdit}
              title="Edytuj umowę"
              className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <Pencil size={14} />
            </button>
            {isOpen ? (
              <button
                type="button"
                onClick={onEnd}
                disabled={endPending}
                title="Zakończ umowę (data: dziś)"
                className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
              >
                <XCircle size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={onReactivate}
                disabled={reactivatePending}
                title="Przywróć jako aktywną (usuwa datę końca)"
                className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
              >
                <RotateCcw size={14} />
              </button>
            )}
            <button
              type="button"
              onClick={onDelete}
              disabled={removePending}
              title="Usuń umowę"
              className="p-1 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 disabled:opacity-50"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Form fields ───────────────────────────────────────────────────────────────

interface FieldBase {
  label: string;
  required?: boolean;
  hint?: string;
}

function FieldDate({
  label, required, hint, value, onChange,
}: FieldBase & { value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
        {label}
        {required && <span className="text-[var(--color-primary)] ml-0.5">*</span>}
        {hint && <span className="ml-1 normal-case text-[var(--color-text-tertiary)] tracking-normal">({hint})</span>}
      </span>
      <DatePicker
        className="border-[var(--color-border)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        value={value}
        onChange={onChange}
      />
    </label>
  );
}

function FieldNumber({
  label, required, suffix, value, onChange,
}: FieldBase & { suffix?: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
        {label}
        {required && <span className="text-[var(--color-primary)] ml-0.5">*</span>}
        {suffix && <span className="ml-1 normal-case text-[var(--color-text-tertiary)] tracking-normal">({suffix})</span>}
      </span>
      <input
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        className="rounded border border-[var(--color-border)] bg-white dark:bg-gray-800 px-2 py-1.5 text-sm font-mono text-right text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function FieldSelect({
  label, required, placeholder, value, onChange, options,
}: FieldBase & {
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wide">
        {label}
        {required && <span className="text-[var(--color-primary)] ml-0.5">*</span>}
      </span>
      <select
        className="rounded border border-[var(--color-border)] bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
